import type { ZoekGroep, ZoekItem } from './zoekTypes';

/** Kleine letters, zonder accenten en leestekens: "Cliënt-dossier" → "client dossier". */
export const normaliseer = (tekst: string): string =>
  tekst
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

// Stopwoorden zodat een hele zin ("hoe maak ik een factuur aan") nog op de kern matcht.
const STOPWOORDEN = new Set([
  'hoe',
  'ik',
  'een',
  'de',
  'het',
  'van',
  'mijn',
  'wil',
  'kan',
  'waar',
  'wat',
  'naar',
  'voor',
  'om',
  'te',
  'je',
  'jij',
  'is',
  'in',
  'op',
  'met',
  'aan',
  'en',
  'of',
  'moet',
  'graag',
  'doe',
  'ga',
  'how',
  'do',
  'i',
  'a',
  'an',
  'the',
  'my',
  'to',
  'where',
  'can',
  'want',
]);

export const zoekTokens = (vraag: string): string[] => {
  const alle = normaliseer(vraag).split(' ').filter(Boolean);
  const kern = alle.filter(w => !STOPWOORDEN.has(w));
  return kern.length ? kern : alle;
};

/** Levenshtein met vroege afbraak; genoeg voor tikfouten in losse woorden. */
const afstand = (a: string, b: string, max: number): number => {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  let vorige = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const huidige = [i];
    let rijMin = i;
    for (let j = 1; j <= b.length; j++) {
      huidige[j] = Math.min(
        vorige[j] + 1,
        huidige[j - 1] + 1,
        vorige[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
      );
      rijMin = Math.min(rijMin, huidige[j]);
    }
    if (rijMin > max) return max + 1;
    vorige = huidige;
  }
  return vorige[b.length];
};

/** Score per token in één veld: 4 = woordbegin, 3 = ergens in het veld, 2 = tikfout, 0 = geen. */
const tokenScore = (token: string, veld: string, woorden: string[]): number => {
  if (woorden.some(w => w.startsWith(token))) return 4;
  if (veld.includes(token)) return 3;
  if (token.length >= 4) {
    const max = token.length >= 8 ? 2 : 1;
    if (
      woorden.some(
        w =>
          afstand(token, w.slice(0, token.length + 1), max) <= max ||
          afstand(token, w, max) <= max
      )
    )
      return 2;
  }
  return 0;
};

const GROEP_BONUS: Record<ZoekGroep, number> = {
  recent: 0,
  taken: 40,
  paginas: 30,
  opDezePagina: 20,
  acties: 15,
  elders: 5,
  vraag: 0,
};

/** Relevantie van een item voor de zoekvraag; 0 = niet tonen. */
export const scoreItem = (
  item: ZoekItem,
  tokens: string[],
  volledigeVraag: string
): number => {
  if (!tokens.length) return 0;
  const titel = normaliseer(item.titel);
  const titelWoorden = titel.split(' ');
  const extra = normaliseer(
    [
      ...(item.trefwoorden ?? []),
      ...(item.pad ?? []),
      item.omschrijving ?? '',
      item.knopTekst ?? '',
    ].join(' ')
  );
  const extraWoorden = extra.split(' ');

  let totaal = 0;
  for (const token of tokens) {
    const inTitel = tokenScore(token, titel, titelWoorden);
    const inExtra = tokenScore(token, extra, extraWoorden);
    const beste = Math.max(inTitel * 30, inExtra * 18);
    if (!beste) return 0;
    totaal += beste;
  }

  const vraag = normaliseer(volledigeVraag);
  if (titel === vraag) totaal += 400;
  else if (titel.startsWith(vraag)) totaal += 250;
  else if (titel.includes(vraag)) totaal += 120;

  // Korte titels die de hele vraag dekken winnen van lange met toevallig dezelfde woorden.
  totaal -= Math.min(titelWoorden.length, 10) * 2;
  return totaal + GROEP_BONUS[item.groep];
};

/** Delen van de titel die met de zoekvraag overeenkomen, voor vetgedrukte weergave. */
export const markeerDelen = (
  titel: string,
  tokens: string[]
): Array<{ tekst: string; match: boolean }> => {
  if (!tokens.length) return [{ tekst: titel, match: false }];
  const plat = titel.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  // NFD kan de lengte veranderen; dan liever niet markeren dan verkeerd markeren.
  if (plat.length !== titel.length) return [{ tekst: titel, match: false }];
  const vlag = new Array<boolean>(titel.length).fill(false);
  for (const token of tokens) {
    let start = plat.indexOf(token);
    while (start !== -1) {
      const vorige = start === 0 ? ' ' : plat[start - 1];
      if (!/[a-z0-9]/.test(vorige)) {
        for (let i = start; i < start + token.length; i++) vlag[i] = true;
        break;
      }
      start = plat.indexOf(token, start + 1);
    }
  }
  const delen: Array<{ tekst: string; match: boolean }> = [];
  for (let i = 0; i < titel.length; i++) {
    const laatste = delen[delen.length - 1];
    if (laatste && laatste.match === vlag[i]) laatste.tekst += titel[i];
    else delen.push({ tekst: titel[i], match: vlag[i] });
  }
  return delen;
};
